import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FooterLayoutDto, HeaderLayoutDto } from '../../apps/airtrfx/commons/types/headerFooterLayout';
import { IAirline, IAirlineSettings } from '../../types/airline';
import { inMenCacheHeaderToken } from '../services/interceptors/cache.interceptor';
import { API_URL } from './apiUrl.provider';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AirlineApiService {

  private _http = inject(HttpClient);
  private _apiUrl = inject<string>(API_URL);

  //#region [main] get Airline

  /**
   * Returns a list of airline ids, codes and names
   */
  getAirlineList(): Observable<IAirline[]> {
    const url = `${this._apiUrl}/api/airline/list`;
    return this._http.get<IAirline[]>(url, {
      headers: new HttpHeaders().append(inMenCacheHeaderToken, "")
    });
  }

  /**
   * Returns the airline settings given an Id
   */
  getAirlineSettings(airlineId: string = ':airline_id:'): Observable<IAirlineSettings> {
    const url = `${this._apiUrl}/api/airlines/settings/${airlineId}`;
    return this._http.get<IAirlineSettings>(url, {
      headers: new HttpHeaders().append(inMenCacheHeaderToken, "")
    });
  }

  /**
   * Returns the airline languages given an airline id
   */
  getAirlineLanguages(airlineId: string = ':airline_id:'): Observable<{ languages: string[] }> {
    const url = `${this._apiUrl}/api/airlines/${airlineId}/languages`;
    return this._http.get<{ languages: string[] }>(url, {
      headers: new HttpHeaders().append(inMenCacheHeaderToken, "")
    });
  }

  //#endregion

  //#region [blue] Menu

  /**
   * Get Airline Menu
   */
  getAirlineMenu(
    airlineId: string = ':airline_id:',
    language: string = ':current_language:'
  ): Observable<unknown> {
    const url = `${this._apiUrl}/api/common/airline/airline_menu/${airlineId}/${language}`;
    return this._http.get<unknown>(url);
  }

  /**
   * Download Header/Footer xls
   * @param menuType `header_menu` or `footer_menu`
   * @param menu Optional, menu counts configuration
   * @param airlineId Airline ID
   * @param language Language
   */
  downloadXlsByMenuType(
    menuType: string,
    menu: { [key: string]: any } = {},
    airlineId: string = ':airline_id:',
    language: string = ':current_language:'
  ): Observable<{ file_name: string }> {
    const url = `${this._apiUrl}/api/common/airline/airline_menu_download_xls/${airlineId}/${language}`;
    return this._http.post<{ file_name: string }>(url, { menu_type: menuType, data: menu });
  }

  /**
   * Uploads a XSL file containing the airline menu.
   */
  uploadXlsByMenuType(
    file: File,
    menuType: string,
    airlineId: string = ':airline_id:',
    language: string = ':current_language:'
  ): Observable<string> {
    const url = `${this._apiUrl}/api/common/airline/airline_menu_upload_xls/${airlineId}/${language}`;
    const formData = new FormData();
    formData.append('menu_type', menuType);
    formData.append('menu_file', file);
    return this._http.post<string>(url, formData);
  }

  /**
   * Save airline menu custom JSON
   */
  saveCustomMenuAsJson(
    menuData: unknown,
    airlineId: string = ':airline_id:',
    language: string = ':current_language:'
  ): Observable<{ message: string }> {
    const url = `${this._apiUrl}/api/common/airline/airline_menu_save_custom_json/${airlineId}/${language}`;
    return this._http.post<{ message: string }>(url, { "content": menuData });
  }

  //#endregion

  //#region [yellow] Header

  /**
   * Calls the API and posts the header design settings for standard airTRFX airlines.
   * @param airlineId airline ID.
   */
  getHeaderLayoutSettings(airlineId: string = ':airline_id:'): Observable<HeaderLayoutDto> {
    const url = `${this._apiUrl}/api/common/header_settings/${airlineId}`;
    return this._http.get<HeaderLayoutDto>(url);
  }

  /**
   * Calls the API and posts the header design settings for standard airTRFX airlines.
   * @param data Header design configuration.
   * @param airlineId airline ID.
   */
  postHeaderLayoutSettings(data: HeaderLayoutDto, airlineId: string = ':airline_id:'): Observable<string> {
    const url = `${this._apiUrl}/api/common/header_settings/${airlineId}`;
    return this._http.post<string>(url, data);
  }

  //#endregion

  //#region [red] Footer

  /**
   * Gets the footer layout settings for the current airline.
   * @param airlineId optional, current airline ID.
   */
  getFooterLayoutSettings(airlineId: string = ':airline_id:'): Observable<FooterLayoutDto> {
    const url = `${this._apiUrl}/api/common/footer_settings/${airlineId}`;
    return this._http.get<FooterLayoutDto>(url);
  }

  /**
   * Posts changes to the footer layout settings for the current airline.
   * @param data changes to the layout
   * @param airlineId optional, current airline ID.
   */
  postFooterLayoutSettings(data: FooterLayoutDto, airlineId: string = ':airline_id:'): Observable<string> {
    const url = `${this._apiUrl}/api/common/footer_settings/${airlineId}`;
    return this._http.post<string>(url, data);
  }

  //#endregion
}
